import { motion } from 'motion/react';

interface FocusCardProps {
  title: string;
  description: string;
  timeRemaining: number; // seconds
  onComplete: () => void;
  onPause: () => void;
}

export function FocusCard({ title, description, timeRemaining, onComplete, onPause }: FocusCardProps) {
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  return (
    <motion.div
      className="focus-card"
      whileHover={{ y: -2, boxShadow: '0 0 0 1px #3b82f6, 0 8px 32px rgba(0, 0, 0, 0.5)' }}
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="badge-info">Active Session</span>
          <div className="font-mono text-lg text-accent-primary tabular-nums">
            {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
          </div>
        </div>
        <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
        <p className="mt-1 text-text-secondary">{description}</p>
      </div>

      {/* Progress ring */}
      <div className="relative mx-auto mb-6" style={{ width: 120, height: 120 }}>
        <svg className="w-full h-full transform -rotate-90">
          <circle
            className="text-border"
            cx="60"
            cy="60"
            r="54"
            strokeWidth="6"
            fill="none"
          />
          <motion.circle
            className="text-accent-primary"
            cx="60"
            cy="60"
            r="54"
            strokeWidth="6"
            strokeDasharray={339.3}
            strokeDashoffset={339.3 * (1 - 0.3)} // 30% progress
            strokeLinecap="round"
            fill="none"
            initial={{ strokeDashoffset: 339.3 }}
            animate={{ strokeDashoffset: 339.3 * (1 - 0.3) }}
            transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="font-mono text-3xl font-bold text-text-primary tabular-nums">
              {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
            </div>
            <div className="text-xs text-text-muted">remaining</div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={onPause}
          className="btn-secondary flex-1"
        >
          <PauseIcon className="w-5 h-5 mr-2" />
          Pause
        </button>
        <button
          onClick={onComplete}
          className="btn-primary flex-1"
        >
          Complete
          <CheckIcon className="w-5 h-5 ml-2" />
        </button>
      </div>
    </motion.div>
  );
}

interface IconProps {
  className?: string;
}

function PauseIcon({ className = '' }: IconProps) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CheckIcon({ className = '' }: IconProps) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}